import { DialogFooter, IExtendedPersonaProps, SelectedPeopleList } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPDefaultButton, IPIconButton, IPModal, IPPrimaryButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { DialogOk } from 'components/Dialogs/DialogOk';
import UserPicker from 'components/Pickers/UserPicker';
import { cancelIcon, globalStackTokensGapMedium } from 'globalStyles';
import Dashboard, { DashboardTypes } from 'models/dashboard';
import User from 'models/user';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocaleDateTimeMedium } from 'utils/datetime';

interface IShareDashboardModalProps {
  currentDashboard: Dashboard | undefined;
  isOpen: boolean;
  onSave: (dashboard: Dashboard) => void;
  onClose: () => void;
}

const ShareDashboardModal = (props: IShareDashboardModalProps) => {
  const { t } = useTranslation(['translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [dashboard, setDashboard] = useState<Dashboard | undefined>(undefined);
  const [showCannotDeleteSharedUser, setShowCannotDeleteSharedUser] = useState<boolean>(false);

  useEffect(() => {
    if (props.isOpen) {
      setDashboard(props.currentDashboard);
    }
  }, [props.currentDashboard, props.isOpen]);

  const hasError = (): boolean => {
    return false;
  };

  const onSave = () => {
    if (!dashboard) return;
    props.onSave(dashboard);
  };

  const getPersonaProps = (): IExtendedPersonaProps[] => {
    if (!dashboard || !dashboard.users) return [];
    const personaProps: IExtendedPersonaProps[] = [];

    for (const user of dashboard.users) {
      if (dashboard.dashboardType === DashboardTypes.user) {
        if (user.id === dashboard.createdById) {
          //filter the creator for user dashboards because the creator is always present in the user list
          continue;
        }
      }

      const personaProp: IExtendedPersonaProps = {
        text: user.name,
        secondaryText: user.email,
        key: user.id,
        isValid: true,
      };

      personaProps.push(personaProp);
    }

    return personaProps;
  };

  const onItemsDeleted = (personaProps: IExtendedPersonaProps[]) => {
    if (!dashboard) return [];
    const newDashboard = dashboard.clone();
    const newUsers = newDashboard.users?.filter((user) => !personaProps.some((p) => p.key === user.id));
    newDashboard.users = newUsers;
    setDashboard(newDashboard);
  };

  const onSelectUser = (item: User | undefined) => {
    if (!dashboard || !item) return [];
    const newDashboard = dashboard.clone();
    newDashboard.users?.push(item);
    setDashboard(newDashboard);
  };

  const onCanRemoveItem = (item: IExtendedPersonaProps): boolean => {
    if (props.currentDashboard?.isSharedWithUser(appContext.user.id)) {
      //can only remove yourself on a shared dashboard
      if (item.key === appContext.user.id) {
        return true;
      } else {
        setShowCannotDeleteSharedUser(true);

        return false;
      }
    }

    return true;
  };

  return (
    <IPModal isBlocking={false} isOpen={props.isOpen} onDismiss={props.onClose}>
      <IPStack verticalFill styles={{ root: { padding: 20, maxWidth: 600 } }} tokens={globalStackTokensGapMedium}>
        <IPStack horizontal horizontalAlign={'space-between'}>
          <IPText variant="xLarge">{t('dashboard:Share.Title')}</IPText>
          <IPIconButton iconProps={cancelIcon} onClick={props.onClose} />
        </IPStack>
        <IPStack.Item>
          <IPText>
            {t('dashboard:Share.SubText', {
              creator:
                dashboard?.createdById !== appContext.user.id
                  ? dashboard?.createdBy?.name
                  : t('dashboard:Share.CreatedByYou'),
              created: toLocaleDateTimeMedium(dashboard?.created),
            })}
          </IPText>
        </IPStack.Item>
        {dashboard?.isShared() && (
          <IPStack.Item>
            <IPText>{t('dashboard:Share.Selected')}</IPText>
          </IPStack.Item>
        )}
        <IPStack.Item>
          <SelectedPeopleList
            selectedItems={getPersonaProps()}
            onItemsDeleted={onItemsDeleted}
            canRemoveItem={onCanRemoveItem}
          />
        </IPStack.Item>
        <IPStack.Item>
          <UserPicker
            onSelect={onSelectUser}
            users={[]}
            placeHolder={t('dashboard:Share.Placeholder')}
            clearAfterSelect={true}
            usersToExclude={dashboard?.users}
          />
        </IPStack.Item>
        <DialogOk
          hidden={!showCannotDeleteSharedUser}
          onOk={() => setShowCannotDeleteSharedUser(false)}
          title={t('dashboard:Share.Dialogs.PreventRemove.Title')}
          subText={t('dashboard:Share.Dialogs.PreventRemove.SubText')}
        ></DialogOk>
        <DialogFooter>
          <IPPrimaryButton onClick={() => onSave()} disabled={hasError()}>
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
          <IPDefaultButton onClick={props.onClose}>{t('translation:General.Button.Cancel')}</IPDefaultButton>
        </DialogFooter>
      </IPStack>
    </IPModal>
  );
};

export default ShareDashboardModal;
